/**
 * Xero|Manus Active
 * APX Hub -- Full-width layout, no left column.
 * KPI strip: Total Clients | Needs Review | Alert | Open Anomalies
 * Full-width Client Roster table below.
 * Clicking "Review" opens an inline right-side anomaly drawer — no navigation.
 */
import React, { useState, useMemo, useRef, useEffect } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  CheckCircle2,
  RefreshCw,
  Search,
  TrendingUp,
  TrendingDown,
  Minus,
  Loader2,
  BarChart3,
  Clock,
  X,
  AlertTriangle,
  Sparkles,
  ChevronDown,
  ChevronUp,
  Printer,
  Copy,
  ExternalLink,
  MessageSquare,
} from "lucide-react";

// --- Design tokens (matches APX shell exactly) --------------------------------
const C = {
  pageBg:       "#EEF4FB",
  headerBg:     "#E2EDF7",
  headerBorder: "#C8D9EC",
  cardBg:       "#FFFFFF",
  cardBorder:   "#C8D9EC",
  cardShadow:   "0 1px 4px rgba(10,30,60,0.07)",
  textPrimary:  "oklch(0.12 0.04 240)",
  textSecond:   "oklch(0.28 0.04 240)",
  textMuted:    "oklch(0.45 0.03 240)",
  textFaint:    "oklch(0.65 0.02 240)",
  gold:         "oklch(0.62 0.14 75)",
  goldLight:    "oklch(0.94 0.06 85)",
  goldBorder:   "oklch(0.80 0.10 75)",
  green:        "oklch(0.45 0.15 155)",
  greenBg:      "oklch(0.94 0.06 155)",
  greenBorder:  "oklch(0.75 0.12 155)",
  amber:        "oklch(0.52 0.14 75)",
  amberBg:      "oklch(0.96 0.06 85)",
  amberBorder:  "oklch(0.80 0.12 85)",
  red:          "oklch(0.50 0.20 25)",
  redBg:        "oklch(0.96 0.05 25)",
  redBorder:    "oklch(0.75 0.16 25)",
  blue:         "oklch(0.45 0.15 240)",
  blueBg:       "oklch(0.94 0.06 240)",
  blueBorder:   "oklch(0.72 0.12 240)",
  tableHead:    "#E2EDF7",
  tableRowHov:  "#EEF4FB",
  tableDiv:     "#C8D9EC",
  rowRed:       "oklch(0.97 0.025 25)",
  rowAmber:     "oklch(0.98 0.020 85)",
};

// --- Helpers ------------------------------------------------------------------

function fmtCurrency(n: number): string {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);
}

function fmtDate(d: Date | string | null): string {
  if (!d) return "Never";
  const date = typeof d === "string" ? new Date(d) : d;
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

function practiceLabel(key: string): string {
  if (key === "apx") return "APX";
  if (key === "apx-hrb") return "HRB";
  if (key === "apx-bbn") return "BBN";
  return key.toUpperCase();
}

function practiceBadgeStyle(key: string): React.CSSProperties {
  if (key === "apx")     return { background: C.goldLight,  color: C.gold,  border: `1px solid ${C.goldBorder}`,  fontWeight: 700 };
  if (key === "apx-hrb") return { background: C.blueBg,     color: C.blue,  border: `1px solid ${C.blueBorder}`,  fontWeight: 700 };
  if (key === "apx-bbn") return { background: C.greenBg,    color: C.green, border: `1px solid ${C.greenBorder}`, fontWeight: 700 };
  return { background: C.tableHead, color: C.textSecond, border: `1px solid ${C.cardBorder}` };
}

function defaultPeriod(): string {
  // Default to the prior completed month (e.g., March 2026 when current month is April 2026)
  const now = new Date();
  const priorMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return `${priorMonth.getFullYear()}-${String(priorMonth.getMonth() + 1).padStart(2, "0")}`;
}

// --- KPI Card -----------------------------------------------------------------

type KpiVariant = "gold" | "amber" | "red" | "orange";

const kpiTheme: Record<KpiVariant, { bg: string; border: string; bar: string; label: string; value: string; sub: string; shadow: string }> = {
  gold:   { bg: "#ffffff", border: "2px solid oklch(0.72 0.14 75)",  bar: `linear-gradient(90deg, ${C.gold}, oklch(0.72 0.14 75))`,   label: C.gold,  value: "oklch(0.28 0.08 75)",  sub: C.amber,    shadow: `0 0 0 1px rgba(0,0,0,0.18), 0 2px 12px oklch(0.62 0.14 75 / 10%)` },
  amber:  { bg: "#ffffff", border: "2px solid oklch(0.72 0.12 80)",  bar: `linear-gradient(90deg, ${C.amber}, oklch(0.62 0.14 80))`,  label: C.amber, value: "oklch(0.28 0.10 80)",  sub: C.textMuted, shadow: `0 0 0 1px rgba(0,0,0,0.18), 0 2px 12px oklch(0.52 0.14 75 / 10%)` },
  red:    { bg: "#ffffff", border: "2px solid oklch(0.55 0.22 25)",  bar: `linear-gradient(90deg, ${C.red}, oklch(0.60 0.20 25))`,    label: C.red,   value: "oklch(0.28 0.12 25)",  sub: C.textMuted, shadow: `0 0 0 1px rgba(0,0,0,0.18), 0 2px 12px oklch(0.50 0.20 25 / 10%)` },
  orange: { bg: "#ffffff", border: "2px solid oklch(0.58 0.18 50)",  bar: `linear-gradient(90deg, oklch(0.58 0.18 50), oklch(0.68 0.16 55))`, label: "oklch(0.50 0.18 50)", value: "oklch(0.28 0.12 50)", sub: C.textMuted, shadow: `0 0 0 1px rgba(0,0,0,0.18), 0 2px 12px oklch(0.58 0.18 50 / 10%)` },
};

function KpiCard({ label, value, sub, variant }: { label: string; value: string | number; sub?: string; variant: KpiVariant }) {
  const t = kpiTheme[variant];
  return (
    <div className="rounded-lg px-4 py-2.5 flex flex-col gap-1 relative overflow-hidden"
      style={{ background: t.bg, border: t.border, boxShadow: t.shadow }}>
      <div className="h-0.5 w-8 rounded-full mb-0.5" style={{ background: t.bar }} />
      <p className="text-xs font-bold uppercase tracking-widest" style={{ color: t.label }}>{label}</p>
      <p className="text-2xl font-bold leading-none" style={{ color: t.value, fontFamily: "'Playfair Display', serif" }}>{value}</p>
      {sub && <p className="text-xs mt-0.5" style={{ color: t.sub }}>{sub}</p>}
    </div>
  );
}

// --- Anomaly Card (inline drawer) ---------------------------------------------

const sevConfig: Record<string, { label: string; bg: string; color: string; border: string }> = {
  high:   { label: "HIGH", bg: C.redBg,   color: C.red,   border: C.redBorder   },
  medium: { label: "MED",  bg: C.amberBg, color: C.amber, border: C.amberBorder },
  low:    { label: "LOW",  bg: C.goldLight, color: C.gold, border: C.goldBorder  },
};

function AnomalyCard({ anomaly, clientName, period }: {
  anomaly: { id: number; anomalyType: string; description: string; amount: number | null; severity: string };
  clientName: string;
  period: string;
}) {
  const [expanded, setExpanded] = useState(false);
  const [suggestion, setSuggestion] = useState<string | null>(null);

  const explainMutation = trpc.intelligence.explainAnomaly.useMutation({
    onSuccess: (res) => setSuggestion(res.explanation),
  });

  function handleGetSuggestion() {
    if (suggestion) { setExpanded(!expanded); return; }
    setExpanded(true);
    explainMutation.mutate({
      clientName,
      anomalyDescription: anomaly.description,
      context: `Period: ${period}. Type: ${anomaly.anomalyType}.${anomaly.amount ? ` Amount: $${anomaly.amount.toFixed(2)}.` : ""}`,
    });
  }

  const sev = sevConfig[anomaly.severity] ?? sevConfig.low;

  return (
    <div className="rounded-lg overflow-hidden"
      style={{ background: C.cardBg, border: `1px solid ${C.cardBorder}`, borderLeft: `3px solid ${sev.color}` }}>
      <div className="px-4 py-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-start gap-2 flex-1 min-w-0">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: sev.color }} />
            <div className="min-w-0">
              <div className="flex items-center gap-1.5 flex-wrap mb-1">
                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-bold uppercase tracking-wider"
                  style={{ background: sev.bg, color: sev.color, border: `1px solid ${sev.border}` }}>
                  {sev.label}
                </span>
                <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: C.textMuted }}>
                  {anomaly.anomalyType.replace(/_/g, " ")}
                </span>
              </div>
              <p className="text-xs leading-relaxed" style={{ color: C.textPrimary }}>{anomaly.description}</p>
              {anomaly.amount !== null && anomaly.amount !== 0 && (
                <p className="text-xs mt-0.5 font-semibold" style={{ color: sev.color }}>
                  ${Math.abs(anomaly.amount).toFixed(2)}
                </p>
              )}
            </div>
          </div>
          <button
            onClick={handleGetSuggestion}
            className="shrink-0 flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold transition-all"
            style={{
              background: expanded ? C.goldLight : C.headerBg,
              border: `1px solid ${expanded ? C.goldBorder : C.cardBorder}`,
              color: expanded ? C.gold : C.textMuted,
            }}
            disabled={explainMutation.isPending}>
            {explainMutation.isPending
              ? <Loader2 className="w-3 h-3 animate-spin" />
              : <Sparkles className="w-3 h-3" />}
            {suggestion ? (expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />) : "AI"}
          </button>
        </div>
      </div>
      {expanded && (
        <div className="px-4 pb-3">
          <div className="rounded p-3" style={{ background: C.goldLight, border: `1px solid ${C.goldBorder}` }}>
            <div className="flex items-center gap-1 mb-1.5">
              <Sparkles className="w-3 h-3" style={{ color: C.gold }} />
              <span className="text-xs font-bold uppercase tracking-wider" style={{ color: C.gold }}>Suggestion</span>
            </div>
            {explainMutation.isPending ? (
              <div className="flex items-center gap-1.5" style={{ color: C.textMuted }}>
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                <span className="text-xs">Analyzing...</span>
              </div>
            ) : (
              <p className="text-xs leading-relaxed" style={{ color: C.textSecond }}>{suggestion}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// --- Suspense Resolution Panel -----------------------------------------------

function SuspenseResolutionPanel({
  clientId, period, xeroTenantId, scrollRef,
}: {
  clientId: string; period: string; xeroTenantId?: string | null; scrollRef?: React.RefObject<HTMLDivElement | null>;
}) {
  const { data: items = [], isLoading } =
    trpc.analytics.getSuspenseItems.useQuery(
      { clientId, period },
      { staleTime: 2 * 60_000 }
    );

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 px-1 py-2" style={{ color: C.textMuted }}>
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
        <span className="text-xs">Scanning for suspense items...</span>
      </div>
    );
  }

  if (items.length === 0) return null;

  // Group by source (Balance Sheet first, then P&L)
  const bsItems = items.filter(i => i.source === "Balance Sheet");
  const plItems = items.filter(i => i.source === "P&L");

  const xeroBase = xeroTenantId
    ? `https://go.xero.com/organisationlogin/default.aspx?shortcode=${xeroTenantId}`
    : "https://go.xero.com";

  function AmountBadge({ amount }: { amount: number }) {
    const isNeg = amount < 0;
    const color = isNeg ? C.red : C.amber;
    const bg = isNeg ? C.redBg : C.amberBg;
    const border = isNeg ? C.redBorder : C.amberBorder;
    return (
      <span className="text-xs font-bold px-2 py-0.5 rounded-full shrink-0"
        style={{ background: bg, color, border: `1px solid ${border}` }}>
        {isNeg ? "-" : "+"}${Math.abs(amount).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      </span>
    );
  }

  function ItemGroup({
    label, groupItems, startNum,
  }: {
    label: string;
    groupItems: typeof items;
    startNum: number;
  }) {
    if (groupItems.length === 0) return null;
    return (
      <div className="mb-2">
        {/* Source label */}
        <div className="flex items-center gap-1.5 mb-1.5 px-1">
          <span className="text-xs font-bold uppercase tracking-wider" style={{ color: C.textMuted }}>
            {label}
          </span>
          <div className="flex-1 h-px" style={{ background: C.cardBorder }} />
        </div>
        <div className="flex flex-col gap-1.5">
          {groupItems.map((item, idx) => (
            <div key={item.id}
              className="rounded-lg px-3 py-2.5 flex items-start gap-2"
              style={{
                background: C.cardBg,
                border: `1px solid ${C.redBorder}`,
                borderLeft: `3px solid ${C.red}`,
              }}>
              {/* Item number */}
              <span className="text-xs font-black shrink-0 w-5 text-center mt-0.5"
                style={{ color: C.red }}>
                {startNum + idx}
              </span>
              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-xs font-semibold leading-tight" style={{ color: C.textPrimary }}>
                    {item.accountName}
                  </p>
                  <AmountBadge amount={item.amount} />
                </div>
                {item.section && (
                  <p className="text-xs mt-0.5" style={{ color: C.textFaint }}>
                    {item.section}
                  </p>
                )}
                {/* Xero deep-link */}
                <a
                  href={xeroBase}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 mt-1.5 text-xs font-semibold transition-colors"
                  style={{ color: C.blue }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.opacity = "0.7"; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.opacity = "1"; }}>
                  <ExternalLink className="w-3 h-3" />
                  Open in Xero to clear
                </a>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div ref={scrollRef} className="rounded-xl overflow-hidden" style={{ border: `1px solid ${C.redBorder}` }}>
      {/* Panel header */}
      <div className="px-3 py-2.5 flex items-center gap-2"
        style={{ background: C.redBg, borderBottom: `1px solid ${C.redBorder}` }}>
        <AlertTriangle className="w-3.5 h-3.5 shrink-0" style={{ color: C.red }} />
        <div className="flex-1 min-w-0">
          <p className="text-xs font-black uppercase tracking-wider" style={{ color: C.red }}>
            Suspense Resolution — {items.length} item{items.length !== 1 ? "s" : ""} to clear
          </p>
          <p className="text-xs mt-0.5" style={{ color: C.textSecond }}>
            These accounts must be cleared before period close
          </p>
        </div>
      </div>
      {/* Item list */}
      <div className="p-3">
        <ItemGroup label="Balance Sheet" groupItems={bsItems} startNum={1} />
        <ItemGroup label="Profit & Loss" groupItems={plItems} startNum={bsItems.length + 1} />
      </div>
    </div>
  );
}

// --- Anomaly Drawer -----------------------------------------------------------

function AnomalyDrawer({
  clientId, clientName, period, xeroTenantId, health, lastPulled, scrollToSuspense, onClose,
}: {
  clientId: string; clientName: string; period: string; xeroTenantId?: string | null; health?: string; lastPulled?: Date | null; scrollToSuspense?: boolean; onClose: () => void;
}) {
  const { data: anomalies = [], isLoading } =
    trpc.analytics.getClientAnomalies.useQuery(
      { clientId, period },
      { enabled: !!clientId, retry: false, staleTime: 60_000 }
    );

  // Ref for the Suspense Resolution Panel — used to auto-scroll after a pull
  const suspensePanelRef = useRef<HTMLDivElement | null>(null);
  const hasScrolled = useRef(false);

  // Auto-scroll to Suspense Resolution panel when scrollToSuspense is true and panel is rendered
  useEffect(() => {
    if (!scrollToSuspense || hasScrolled.current) return;
    // Wait for the panel to mount and have content
    const timer = setTimeout(() => {
      if (suspensePanelRef.current) {
        suspensePanelRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
        hasScrolled.current = true;
      }
    }, 600); // allow query + render to settle
    return () => clearTimeout(timer);
  }, [scrollToSuspense, suspensePanelRef.current]);

  const escalateMutation = trpc.analytics.escalateToOwner.useMutation({
    onSuccess: (data) => {
      toast.success(data.method === "sms" ? "SMS sent to owner" : "Email sent to owner");
      setEscalateOpen(false);
    },
    onError: (e) => toast.error(`Escalation failed: ${e.message}`),
  });

  // Escalate modal state
  const [escalateOpen, setEscalateOpen] = React.useState(false);
  const [escalateMethod, setEscalateMethod] = React.useState<"sms" | "email">("sms");
  const [escalatePhone, setEscalatePhone] = React.useState("");
  const [escalateEmail, setEscalateEmail] = React.useState("");
  const [escalateSave, setEscalateSave] = React.useState(false);

  // Load saved contact info from app settings
  const { data: savedSettings } = trpc.appSettings.getMany.useQuery(
    { keys: ["ownerPhone", "ownerEmail"] },
    { staleTime: 5 * 60_000 }
  );
  React.useEffect(() => {
    if (savedSettings?.ownerPhone && !escalatePhone) setEscalatePhone(savedSettings.ownerPhone);
    if (savedSettings?.ownerEmail && !escalateEmail) setEscalateEmail(savedSettings.ownerEmail);
  }, [savedSettings]);

  function handleEscalateSubmit() {
    if (anomalies.length === 0) return;
    escalateMutation.mutate({
      clientName,
      period,
      anomalies: anomalies.map(a => ({
        severity: a.severity,
        anomalyType: a.anomalyType,
        description: a.description,
        amount: a.amount,
      })),
      method: escalateMethod,
      ownerPhone: escalateMethod === "sms" ? escalatePhone : undefined,
      ownerEmail: escalateMethod === "email" ? escalateEmail : undefined,
      saveContact: escalateSave,
    });
  }

  const periodLabel = period
    ? new Date(period + "-01").toLocaleDateString("en-US", { month: "long", year: "numeric" })
    : "";

  const highCount = anomalies.filter((a) => a.severity === "high").length;
  const medCount  = anomalies.filter((a) => a.severity === "medium").length;
  const lowCount  = anomalies.filter((a) => a.severity === "low").length;

  // Build Xero deep-link for this client's org
  const xeroUrl = xeroTenantId
    ? `https://go.xero.com/organisationlogin/default.aspx?shortcode=${xeroTenantId}`
    : "https://go.xero.com";

  function handlePrint() {
    if (anomalies.length === 0) return;
    const rows = anomalies.map(a => {
      const amt = a.amount ? ` — $${Math.abs(a.amount).toLocaleString("en-US", { maximumFractionDigits: 0 })}` : "";
      return `<tr>
        <td style="padding:6px 10px;border-bottom:1px solid #e8e3dc;font-size:12px;color:#1a2540">${a.description}${amt}</td>
        <td style="padding:6px 10px;border-bottom:1px solid #e8e3dc;font-size:12px;text-align:center">
          <span style="background:${a.severity==='high'?'#fef2f2':a.severity==='medium'?'#fffbeb':'#fffdf0'};color:${a.severity==='high'?'#dc2626':a.severity==='medium'?'#d97706':'#b8963e'};padding:2px 8px;border-radius:12px;font-weight:700;font-size:11px">${a.severity.toUpperCase()}</span>
        </td>
        <td style="padding:6px 10px;border-bottom:1px solid #e8e3dc;font-size:12px;color:#6b7280">${a.anomalyType.replace(/_/g, ' ')}</td>
      </tr>`;
    }).join("");
    const html = `<!DOCTYPE html><html><head><title>Anomaly Report — ${clientName}</title>
      <style>body{font-family:'Helvetica Neue',Arial,sans-serif;margin:0;padding:24px;color:#1a2540}
      h2{font-size:18px;margin:0 0 4px}p{font-size:12px;color:#6b7280;margin:0 0 16px}
      table{width:100%;border-collapse:collapse}th{background:#ede8e0;padding:8px 10px;text-align:left;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.05em;color:#6b7280}
      @media print{body{padding:0}}</style></head>
      <body>
        <h2>Anomaly Report — ${clientName}</h2>
        <p>Period: ${periodLabel} &nbsp;·&nbsp; ${anomalies.length} item${anomalies.length!==1?'s':''} &nbsp;·&nbsp; Printed ${new Date().toLocaleDateString()}</p>
        <table><thead><tr><th>Description</th><th style="text-align:center">Severity</th><th>Type</th></tr></thead><tbody>${rows}</tbody></table>
        <p style="margin-top:16px;font-size:11px;color:#9ca3af">Fix in Xero — next pull clears automatically. APX WorkCenter.</p>
      </body></html>`;
    const w = window.open("", "_blank", "width=800,height=600");
    if (!w) { toast.error("Pop-up blocked — allow pop-ups to print"); return; }
    w.document.write(html);
    w.document.close();
    w.onload = () => w.print();
  }

  function handleCopy() {
    if (anomalies.length === 0) return;
    const lines = [
      `Anomaly Report — ${clientName} (${periodLabel})`,
      `${anomalies.length} item${anomalies.length!==1?'s':''}`,
      "",
      ...anomalies.map(a => {
        const amt = a.amount ? ` ($${Math.abs(a.amount).toLocaleString("en-US", { maximumFractionDigits: 0 })})` : "";
        return `[${a.severity.toUpperCase()}] ${a.description}${amt}`;
      }),
      "",
      "Fix in Xero — next pull clears automatically.",
    ];
    navigator.clipboard.writeText(lines.join("\n")).then(
      () => toast.success("Copied to clipboard"),
      () => toast.error("Copy failed")
    );
  }

  // handleEscalateSubmit is defined above

  const iconBtn: React.CSSProperties = {
    height: 28, padding: "0 8px", borderRadius: 6, fontSize: 11, fontWeight: 600,
    display: "flex", alignItems: "center", gap: 4, cursor: "pointer", transition: "all 0.15s",
    background: C.cardBg, border: `1px solid ${C.cardBorder}`, color: C.textMuted,
  };

  return (
    <div
      className="flex flex-col h-full"
      style={{ width: 380, background: C.pageBg, borderLeft: `1px solid ${C.headerBorder}`, flexShrink: 0 }}>

      {/* Drawer header */}
      <div className="px-4 py-3 flex items-start justify-between gap-2"
        style={{ background: C.headerBg, borderBottom: `1px solid ${C.headerBorder}` }}>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-bold uppercase tracking-widest mb-0.5" style={{ color: C.textMuted }}>Anomalies</p>
          <h3 className="font-bold text-sm leading-tight truncate" style={{ fontFamily: "'Playfair Display', serif", color: C.textPrimary }}>
            {clientName}
          </h3>
          <p className="text-xs mt-0.5" style={{ color: C.textMuted }}>{periodLabel}</p>
          {anomalies.length > 0 && (
            <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
              {highCount > 0 && (
                <span className="text-xs font-bold px-1.5 py-0.5 rounded-full"
                  style={{ background: C.redBg, color: C.red, border: `1px solid ${C.redBorder}` }}>
                  {highCount} high
                </span>
              )}
              {medCount > 0 && (
                <span className="text-xs font-bold px-1.5 py-0.5 rounded-full"
                  style={{ background: C.amberBg, color: C.amber, border: `1px solid ${C.amberBorder}` }}>
                  {medCount} med
                </span>
              )}
              {lowCount > 0 && (
                <span className="text-xs font-bold px-1.5 py-0.5 rounded-full"
                  style={{ background: C.goldLight, color: C.gold, border: `1px solid ${C.goldBorder}` }}>
                  {lowCount} low
                </span>
              )}
            </div>
          )}
        </div>
        <button onClick={onClose}
          className="shrink-0 w-7 h-7 flex items-center justify-center rounded-lg transition-colors"
          style={{ background: C.cardBg, border: `1px solid ${C.cardBorder}`, color: C.textMuted }}
          onMouseEnter={(e) => { (e.currentTarget.style.background = C.redBg); (e.currentTarget.style.color = C.red); }}
          onMouseLeave={(e) => { (e.currentTarget.style.background = C.cardBg); (e.currentTarget.style.color = C.textMuted); }}>
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Action toolbar — only shown when there are anomalies */}
      {!isLoading && anomalies.length > 0 && (
        <div className="px-3 py-2 flex items-center gap-1.5 flex-wrap"
          style={{ background: C.headerBg, borderBottom: `1px solid ${C.headerBorder}` }}>
          {/* Open in Xero */}
          <Tooltip>
            <TooltipTrigger asChild>
              <a href={xeroUrl} target="_blank" rel="noopener noreferrer"
                style={iconBtn}
                onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = C.blueBg; (e.currentTarget as HTMLElement).style.color = C.blue; (e.currentTarget as HTMLElement).style.borderColor = C.blueBorder; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = C.cardBg; (e.currentTarget as HTMLElement).style.color = C.textMuted; (e.currentTarget as HTMLElement).style.borderColor = C.cardBorder; }}>
                <ExternalLink className="w-3 h-3" />
                Xero
              </a>
            </TooltipTrigger>
            <TooltipContent>Open client in Xero to fix</TooltipContent>
          </Tooltip>

          {/* Copy */}
          <Tooltip>
            <TooltipTrigger asChild>
              <button onClick={handleCopy} style={iconBtn}
                onMouseEnter={(e) => { e.currentTarget.style.background = C.goldLight; e.currentTarget.style.color = C.gold; e.currentTarget.style.borderColor = C.goldBorder; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = C.cardBg; e.currentTarget.style.color = C.textMuted; e.currentTarget.style.borderColor = C.cardBorder; }}>
                <Copy className="w-3 h-3" />
                Copy
              </button>
            </TooltipTrigger>
            <TooltipContent>Copy anomaly list to clipboard</TooltipContent>
          </Tooltip>

          {/* Print */}
          <Tooltip>
            <TooltipTrigger asChild>
              <button onClick={handlePrint} style={iconBtn}
                onMouseEnter={(e) => { e.currentTarget.style.background = C.goldLight; e.currentTarget.style.color = C.gold; e.currentTarget.style.borderColor = C.goldBorder; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = C.cardBg; e.currentTarget.style.color = C.textMuted; e.currentTarget.style.borderColor = C.cardBorder; }}>
                <Printer className="w-3 h-3" />
                Print
              </button>
            </TooltipTrigger>
            <TooltipContent>Print anomaly report</TooltipContent>
          </Tooltip>

          {/* Escalate to Owner */}
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={() => setEscalateOpen(true)}
                style={iconBtn}
                onMouseEnter={(e) => { e.currentTarget.style.background = C.amberBg; e.currentTarget.style.color = C.amber; e.currentTarget.style.borderColor = C.amberBorder; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = C.cardBg; e.currentTarget.style.color = C.textMuted; e.currentTarget.style.borderColor = C.cardBorder; }}>
                <MessageSquare className="w-3 h-3" />
                Escalate
              </button>
            </TooltipTrigger>
            <TooltipContent>Text or email anomaly summary to owner</TooltipContent>
          </Tooltip>

          {/* Escalate modal */}
          {escalateOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: "rgba(0,0,0,0.45)" }}
              onClick={(e) => { if (e.target === e.currentTarget) setEscalateOpen(false); }}>
              <div className="rounded-xl shadow-2xl p-5 w-80" style={{ background: C.pageBg, border: `1px solid ${C.cardBorder}` }}>
                <h4 className="font-bold text-sm mb-1" style={{ color: C.textPrimary, fontFamily: "'Playfair Display', serif" }}>Escalate to Owner</h4>
                <p className="text-xs mb-3" style={{ color: C.textMuted }}>{clientName} &middot; {anomalies.length} anomal{anomalies.length !== 1 ? "ies" : "y"}</p>

                {/* Method toggle */}
                <div className="flex gap-1.5 mb-3">
                  {(["sms", "email"] as const).map(m => (
                    <button key={m} onClick={() => setEscalateMethod(m)}
                      className="flex-1 py-1.5 rounded-lg text-xs font-bold transition-all"
                      style={escalateMethod === m
                        ? { background: C.amber, color: "#fff", border: `1px solid ${C.amber}` }
                        : { background: C.cardBg, color: C.textMuted, border: `1px solid ${C.cardBorder}` }}>
                      {m === "sms" ? "📱 Text" : "✉️ Email"}
                    </button>
                  ))}
                </div>

                {/* Contact input */}
                {escalateMethod === "sms" ? (
                  <div className="mb-3">
                    <label className="text-xs font-semibold block mb-1" style={{ color: C.textMuted }}>Phone Number</label>
                    <input
                      type="tel" placeholder="+1 (310) 555-0000"
                      value={escalatePhone}
                      onChange={e => setEscalatePhone(e.target.value)}
                      className="w-full text-xs px-3 py-2 rounded-lg outline-none"
                      style={{ background: C.cardBg, border: `1px solid ${C.cardBorder}`, color: C.textPrimary }} />
                  </div>
                ) : (
                  <div className="mb-3">
                    <label className="text-xs font-semibold block mb-1" style={{ color: C.textMuted }}>Email Address</label>
                    <input
                      type="email" placeholder="owner@example.com"
                      value={escalateEmail}
                      onChange={e => setEscalateEmail(e.target.value)}
                      className="w-full text-xs px-3 py-2 rounded-lg outline-none"
                      style={{ background: C.cardBg, border: `1px solid ${C.cardBorder}`, color: C.textPrimary }} />
                  </div>
                )}

                {/* Save for next time */}
                <label className="flex items-center gap-2 mb-4 cursor-pointer">
                  <input type="checkbox" checked={escalateSave} onChange={e => setEscalateSave(e.target.checked)}
                    className="rounded" />
                  <span className="text-xs" style={{ color: C.textMuted }}>Save for next time</span>
                </label>

                {/* Actions */}
                <div className="flex gap-2">
                  <button onClick={() => setEscalateOpen(false)}
                    className="flex-1 py-1.5 rounded-lg text-xs font-bold"
                    style={{ background: C.cardBg, color: C.textMuted, border: `1px solid ${C.cardBorder}` }}>
                    Cancel
                  </button>
                  <button onClick={handleEscalateSubmit}
                    disabled={escalateMutation.isPending || (escalateMethod === "sms" ? !escalatePhone : !escalateEmail)}
                    className="flex-1 py-1.5 rounded-lg text-xs font-bold transition-all"
                    style={{ background: C.amber, color: "#fff", border: `1px solid ${C.amber}`, opacity: escalateMutation.isPending ? 0.6 : 1 }}>
                    {escalateMutation.isPending
                      ? <span className="flex items-center justify-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Sending...</span>
                      : `Send ${escalateMethod === "sms" ? "Text" : "Email"}`}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Drawer body */}
      <div className="flex-1 overflow-y-auto px-3 py-3 flex flex-col gap-2">
        {isLoading ? (
          <div className="flex items-center justify-center h-32 gap-2" style={{ color: C.textMuted }}>
            <Loader2 className="w-4 h-4 animate-spin" />
            <span className="text-xs">Loading anomalies...</span>
          </div>
        ) : anomalies.length === 0 ? (
          health === "pending" ? (
            <div className="flex flex-col items-center justify-center h-36 gap-2 rounded-xl"
              style={{ background: C.amberBg, border: `1px solid ${C.amberBorder}` }}>
              <div className="w-8 h-8 rounded-full flex items-center justify-center"
                style={{ background: "#fff" }}>
                <Clock className="w-4 h-4" style={{ color: C.amber }} />
              </div>
              <p className="text-xs font-semibold" style={{ color: C.amber }}>No data pulled yet</p>
              <p className="text-xs text-center px-4" style={{ color: C.textSecond }}>
                Click <strong>Pull Data</strong> on this client row to fetch {periodLabel} data from Xero
              </p>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-32 gap-2 rounded-xl"
              style={{ background: C.cardBg, border: `1px solid ${C.cardBorder}` }}>
              <div className="w-8 h-8 rounded-full flex items-center justify-center"
                style={{ background: C.greenBg }}>
                <CheckCircle2 className="w-4 h-4" style={{ color: C.green }} />
              </div>
              <p className="text-xs font-semibold" style={{ color: C.textSecond }}>No open anomalies</p>
              <p className="text-xs text-center px-4" style={{ color: C.textFaint }}>This client is clear for {periodLabel}</p>
            </div>
          )
        ) : (
          <>
            <p className="text-xs px-1" style={{ color: C.textMuted }}>
              Fix in Xero — next pull clears automatically
            </p>
            {anomalies.map((a) => (
              <AnomalyCard
                key={a.id}
                anomaly={a}
                clientName={clientName}
                period={period}
              />
            ))}
          </>
        )}

        {/* Suspense Resolution Panel — always shown if items exist, regardless of other anomalies */}
        <SuspenseResolutionPanel
          clientId={clientId}
          period={period}
          xeroTenantId={xeroTenantId}
          scrollRef={suspensePanelRef}
        />
      </div>
    </div>
  );
}
// --- Main Page -----------------------------------------------------------------

export default function ClientAnalytics() {
  const { user } = useAuth();

  const [period, setPeriod] = useState(defaultPeriod);
  const [search, setSearch] = useState("");
  const [practiceFilter, setPracticeFilter] = useState("all");
  const [activeTab, setActiveTab] = useState<"all" | "mine" | "action">("all");

  // Inline anomaly drawer state
  const [drawerClient, setDrawerClient] = useState<{ clientId: string; clientName: string; xeroTenantId?: string | null; lastPulled?: Date | null; health?: string; scrollToSuspense?: boolean } | null>(null);

  const { data: summaries = [], isLoading: loadingSummaries, refetch: refetchSummaries } =
    trpc.analytics.getSummaries.useQuery({ period });

  const { data: openAnomalies = [] } =
    trpc.analytics.getAllOpenAnomalies.useQuery({ period }, { retry: false, staleTime: 120_000 });

  const pullClientMutation = trpc.analytics.pullClient.useMutation({
    onSuccess: (res) => {
      const suspenseMsg = res.suspenseItemsFound > 0
        ? ` · ${res.suspenseItemsFound} suspense item${res.suspenseItemsFound !== 1 ? "s" : ""} found`
        : "";
      toast.success(`${res.clientName}: data pulled (${res.anomaliesFound} anomalies${suspenseMsg})`);
      refetchSummaries();
      // If suspense items were found, auto-open the drawer and scroll to the Suspense Resolution panel
      if (res.suspenseItemsFound > 0) {
        const pulledRow = summaries.find(s => s.clientId === res.clientId);
        setDrawerClient({
          clientId: String(res.clientId),
          clientName: res.clientName,
          xeroTenantId: (pulledRow as { xeroTenantId?: string | null } | undefined)?.xeroTenantId ?? null,
          health: "yellow",
          scrollToSuspense: true,
        });
      }
    },
    onError: (e) => toast.error(`Pull failed: ${e.message}`),
  });

  const pullAllMutation = trpc.analytics.pullAll.useMutation({
    onSuccess: (res) => { toast.success(`Pulled ${res.success}/${res.total} clients`); refetchSummaries(); },
    onError: (e) => toast.error(`Pull all failed: ${e.message}`),
  });

  const stats = useMemo(() => {
    const total = summaries.length;
    const needsReview = summaries.filter((s) => s.health === "yellow").length;
    const alert = summaries.filter((s) => s.health === "red").length;
    return { total, needsReview, alert };
  }, [summaries]);

  const anomalyCounts = useMemo(() => {
    const high = openAnomalies.filter((a) => a.severity === "high").length;
    const med  = openAnomalies.filter((a) => a.severity === "medium").length;
    const low  = openAnomalies.filter((a) => a.severity === "low").length;
    return { high, med, low, total: openAnomalies.length };
  }, [openAnomalies]);

  // Map each staff email to the name codes stored in core_clients t1Bookkeeper/t2Bookkeeper columns.
  // These codes are short uppercase identifiers used during data entry (not full names).
  const BOOKKEEPER_CODE_MAP: Record<string, string[]> = {
    "lourdesmastache20@gmail.com":    ["LULU", "JANY/LULU"],
    "iritna@gmail.com":             ["IRI", "IRIT"],
    "lopez.jany@gmail.com":         ["JANY", "JANY/LULU"],
    "marianjoyhernandez2@gmail.com":["MARIAN", "MARAIN"],
    "yunuenalfaro7@gmail.com":      ["YUNUEN"],
    "shobhit@advancefinserv.com":   ["SHOBHIT"],
    "erenatalazo@gmail.com":        ["RENATA"],
    "arvind@advancefinserv.com":     ["ARVIND"],
    "gus@bbnfi.com":                ["GUSTAVO", "GUS"],
  };

  const myBookkeeperCodes = useMemo(() => {
    if (!user?.email) return [] as string[];
    return BOOKKEEPER_CODE_MAP[user.email.toLowerCase()] ?? [];
  }, [user]);

  const filtered = useMemo(() => {
    let rows = summaries;
    if (practiceFilter !== "all") rows = rows.filter((r) => r.practiceKey === practiceFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      rows = rows.filter((r) => r.clientName.toLowerCase().includes(q));
    }
    if (activeTab === "mine") {
      if (myBookkeeperCodes.length === 0) {
        // Fallback: match first name fragment for unmapped users
        const fragment = (user?.name ?? "").split(" ")[0].toUpperCase();
        rows = rows.filter((r) =>
          (r.t1Bookkeeper && r.t1Bookkeeper.toUpperCase().includes(fragment)) ||
          (r.t2Bookkeeper && r.t2Bookkeeper.toUpperCase().includes(fragment))
        );
      } else {
        rows = rows.filter((r) => {
          const t1 = (r.t1Bookkeeper ?? "").toUpperCase();
          const t2 = (r.t2Bookkeeper ?? "").toUpperCase();
          return myBookkeeperCodes.some(code => t1 === code || t1.includes(code) || t2 === code || t2.includes(code));
        });
      }
    } else if (activeTab === "action") {
      rows = rows.filter((r) => r.health === "red" || r.health === "yellow");
    }
    return rows;
  }, [summaries, practiceFilter, search, activeTab, myBookkeeperCodes, user]);

  const actionCount = useMemo(
    () => summaries.filter((r) => r.health === "red" || r.health === "yellow").length,
    [summaries]
  );

  const periodOptions = useMemo(() => {
    const opts: { value: string; label: string }[] = [];
    const now = new Date();
    for (let i = 0; i < 7; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      const label = d.toLocaleDateString("en-US", { month: "long", year: "numeric" });
      opts.push({ value: val, label });
    }
    return opts;
  }, []);

  const isManager = ["admin", "super_admin", "controller", "bookkeeping_manager", "bookkeeper_supervisor"].includes(user?.role ?? "");

  function rowBg(health: string) {
    if (health === "red")    return C.rowRed;
    if (health === "yellow") return C.rowAmber;
    return C.cardBg;
  }

  const actionBtn: React.CSSProperties = {
    height: 28, padding: "0 10px", borderRadius: 6, fontSize: 12, fontWeight: 600,
    background: "oklch(0.97 0.010 240)", border: `1px solid ${C.cardBorder}`,
    color: C.textSecond, cursor: "pointer", transition: "all 0.15s",
  };

  function handleReview(clientId: string, clientName: string, xeroTenantId?: string | null, lastPulled?: Date | null, health?: string) {
    // Toggle: clicking the same client again closes the drawer
    if (drawerClient?.clientId === clientId) {
      setDrawerClient(null);
    } else {
      setDrawerClient({ clientId, clientName, xeroTenantId, lastPulled, health });
    }
  }

  return (
    <DashboardLayout noPadding>
    <div className="flex flex-col h-full min-h-screen" style={{ background: C.pageBg }}>

      {/* Page Header */}
      <div className="px-6 py-2 flex items-center justify-between gap-4 flex-wrap"
        style={{ background: C.headerBg, borderBottom: `1px solid ${C.headerBorder}`, boxShadow: "0 1px 4px rgba(10,20,40,0.06)" }}>
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-bold tracking-tight" style={{ fontFamily: "'Playfair Display', serif", color: C.textPrimary }}>
              Xero|Manus Active
            </h1>
            <span className="text-xs font-bold uppercase tracking-widest px-2.5 py-0.5 rounded-full"
              style={{ background: C.goldLight, color: C.gold, border: `1px solid ${C.goldBorder}` }}>
              LIVE
            </span>
          </div>
          <p className="text-xs mt-0.5" style={{ color: C.textMuted }}>
            Operational health &amp; action center
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-44 h-9 text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              {periodOptions.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {isManager && (
            <button className="apx-btn-gold h-9 px-4 text-sm flex items-center gap-1.5"
              onClick={() => pullAllMutation.mutate({ period })}
              disabled={pullAllMutation.isPending}>
              {pullAllMutation.isPending
                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                : <RefreshCw className="w-3.5 h-3.5" />}
              Pull All
            </button>
          )}
        </div>
      </div>

      {/* KPI Strip -- 4 cards */}
      <div className="px-6 pt-2 pb-2 grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard
          label="Total Clients"
          value={stats.total}
          sub={`APX ${summaries.filter((s) => s.practiceKey === "apx").length} . HRB ${summaries.filter((s) => s.practiceKey === "apx-hrb").length} . BBN ${summaries.filter((s) => s.practiceKey === "apx-bbn").length}`}
          variant="gold"
        />
        <KpiCard
          label="Needs Review"
          value={stats.needsReview}
          sub="Assigned to bookkeeper"
          variant="amber"
        />
        <KpiCard
          label="Alert"
          value={stats.alert}
          sub="Escalation required"
          variant="red"
        />
        <KpiCard
          label="Open Anomalies"
          value={anomalyCounts.total}
          sub={`${anomalyCounts.high} high . ${anomalyCounts.med} med . ${anomalyCounts.low} low`}
          variant={anomalyCounts.high > 0 ? "red" : anomalyCounts.med > 0 ? "orange" : "amber"}
        />
      </div>

      {/* Main content: roster + optional inline drawer */}
      <div className="flex-1 flex gap-0 min-h-0 overflow-hidden">

        {/* Full-width Client Roster */}
        <div className="flex-1 flex flex-col min-w-0" style={{ background: C.cardBg }}>

          {/* Toolbar */}
          <div className="px-5 pt-2 pb-0 space-y-2" style={{ borderBottom: `1px solid ${C.tableDiv}` }}>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <h2 className="font-bold text-base" style={{ fontFamily: "'Playfair Display', serif", color: C.textPrimary }}>
                Client Roster{" "}
                <span className="text-xs font-normal ml-1" style={{ color: C.textMuted }}>{filtered.length} clients</span>
              </h2>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5" style={{ color: C.textFaint }} />
                  <input
                    placeholder="Search client name..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-8 h-8 w-56 text-sm rounded-md outline-none transition-all"
                    style={{ background: C.tableHead, border: `1px solid ${C.cardBorder}`, color: C.textPrimary }}
                    onFocus={(e) => (e.currentTarget.style.borderColor = C.goldBorder)}
                    onBlur={(e)  => (e.currentTarget.style.borderColor = C.cardBorder)}
                  />
                </div>
                <Select value={practiceFilter} onValueChange={setPracticeFilter}>
                  <SelectTrigger className="h-8 w-36 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Practices</SelectItem>
                    <SelectItem value="apx">APX</SelectItem>
                    <SelectItem value="apx-hrb">HRB</SelectItem>
                    <SelectItem value="apx-bbn">BBN</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-1">
              {[
                { id: "all",    label: "All Clients" },
                { id: "mine",   label: "My Clients" },
                { id: "action", label: "Action Required", count: actionCount },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as typeof activeTab)}
                  className="px-3 py-2 text-sm font-semibold border-b-2 transition-colors"
                  style={activeTab === tab.id
                    ? { borderColor: C.gold, color: C.gold }
                    : { borderColor: "transparent", color: C.textMuted }}>
                  {tab.label}
                  {tab.count !== undefined && tab.count > 0 && (
                    <span className="ml-1.5 text-xs font-bold px-1.5 py-0.5 rounded-full"
                      style={{ background: C.redBg, color: C.red }}>
                      {tab.count}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Table */}
          <div className="overflow-auto flex-1">
            {loadingSummaries ? (
              <div className="flex items-center justify-center h-40 gap-2" style={{ color: C.textMuted }}>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span className="text-sm">Loading client data...</span>
              </div>
            ) : filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-40 gap-2" style={{ color: C.textMuted }}>
                <BarChart3 className="w-8 h-8 opacity-30" />
                <p className="text-sm">No clients match the current filters</p>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ borderBottom: `2px solid ${C.tableDiv}`, background: C.tableHead, position: "sticky", top: 0, zIndex: 10 }}>
                    {[
                      { label: "Client",       align: "left",   w: "22%" },
                      { label: "Practice",     align: "left",   w: "8%"  },
                      { label: "Bookkeepers",  align: "left",   w: "14%" },
                      { label: "Income",       align: "right",  w: "11%" },
                      { label: "Expenses",     align: "right",  w: "11%" },
                      { label: "Net Profit",   align: "right",  w: "11%" },
                      { label: "Anomalies",    align: "center", w: "9%"  },
                      { label: "Actions",      align: "right",  w: "14%" },
                    ].map((h) => (
                      <th key={h.label}
                        className={`px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-${h.align}`}
                        style={{ color: C.textMuted, width: h.w }}>
                        {h.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((row) => {
                    const isPending = row.health === "pending";
                    const base = rowBg(row.health);
                    const isActive = drawerClient?.clientId === String(row.clientId);
                    return (
                      <tr key={row.clientId}
                        style={{
                          background: isActive ? C.goldLight : base,
                          borderBottom: `1px solid ${C.tableDiv}`,
                          borderLeft: `3px solid ${isActive ? C.gold : row.health === "red" ? C.red : row.health === "yellow" ? C.amber : "transparent"}`,
                        }}
                        onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = C.tableRowHov; }}
                        onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = base; }}>

                        {/* Client */}
                        <td className="px-4 py-3">
                          <p className="font-semibold leading-tight truncate" style={{ color: C.textPrimary }}>{row.clientName}</p>
                          <p className="text-xs mt-0.5 flex items-center gap-1" style={{ color: C.textFaint }}>
                            <Clock className="w-3 h-3" />
                            {isPending
                              ? <span style={{ color: C.amber, fontWeight: 600 }}>Never pulled</span>
                              : fmtDate(row.lastPulled)}
                          </p>
                        </td>

                        {/* Practice */}
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs" style={practiceBadgeStyle(row.practiceKey)}>
                            {practiceLabel(row.practiceKey)}
                          </span>
                        </td>

                        {/* Bookkeepers */}
                        <td className="px-4 py-3">
                          <div className="flex flex-col gap-0.5">
                            {row.t1Bookkeeper
                              ? <span className="text-xs"><span style={{ color: C.textFaint }}>Bkkpr 1 </span><span className="font-medium" style={{ color: C.textSecond }}>{row.t1Bookkeeper}</span></span>
                              : <span className="text-xs" style={{ color: C.textFaint }}>Bkkpr 1 --</span>}
                            {row.t2Bookkeeper
                              ? <span className="text-xs"><span style={{ color: C.textFaint }}>Bkkpr 2 </span><span className="font-medium" style={{ color: C.textSecond }}>{row.t2Bookkeeper}</span></span>
                              : <span className="text-xs" style={{ color: C.textFaint }}>Bkkpr 2 --</span>}
                          </div>
                        </td>

                        {/* Income */}
                        <td className="px-4 py-3 text-right">
                          {isPending
                            ? <span style={{ color: C.textFaint }}>--</span>
                            : <span style={{ color: C.textSecond }}>{fmtCurrency(row.totalIncome)}</span>}
                        </td>

                        {/* Expenses */}
                        <td className="px-4 py-3 text-right">
                          {isPending
                            ? <span style={{ color: C.textFaint }}>--</span>
                            : <span style={{ color: C.red }}>({fmtCurrency(Math.abs(row.totalExpenses))})</span>}
                        </td>

                        {/* Net Profit */}
                        <td className="px-4 py-3 text-right">
                          {isPending ? <span style={{ color: C.textFaint }}>--</span> : (
                            row.netProfit < 0
                              ? <span className="inline-flex items-center gap-0.5 font-semibold" style={{ color: C.red }}>
                                  <TrendingDown className="w-3 h-3" />{fmtCurrency(row.netProfit)}
                                </span>
                              : row.netProfit > 0
                                ? <span className="inline-flex items-center gap-0.5 font-medium" style={{ color: C.green }}>
                                    <TrendingUp className="w-3 h-3" />{fmtCurrency(row.netProfit)}
                                  </span>
                                : <span className="inline-flex items-center gap-0.5" style={{ color: C.textFaint }}>
                                    <Minus className="w-3 h-3" />$0
                                  </span>
                          )}
                        </td>

                        {/* Anomalies */}
                        <td className="px-4 py-3 text-center">
                          {row.anomalyCount > 0 ? (
                            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold"
                              style={row.health === "red"
                                ? { background: C.redBg,   color: C.red,   border: `1px solid ${C.redBorder}`   }
                                : { background: C.amberBg, color: C.amber, border: `1px solid ${C.amberBorder}` }}>
                              {row.anomalyCount}
                            </span>
                          ) : isPending
                            ? <span style={{ color: C.textFaint }}>--</span>
                            : <CheckCircle2 className="w-4 h-4 mx-auto" style={{ color: C.green }} />}
                        </td>

                        {/* Actions */}
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1">
                            {isPending ? (
                              <button style={{ ...actionBtn, color: C.blue, borderColor: C.blueBorder }}
                                onMouseEnter={(e) => { (e.currentTarget.style.background = C.blueBg); }}
                                onMouseLeave={(e) => { (e.currentTarget.style.background = actionBtn.background as string); }}
                                onClick={() => pullClientMutation.mutate({ clientId: row.clientId, period })}
                                disabled={pullClientMutation.isPending}>
                                {pullClientMutation.isPending && pullClientMutation.variables?.clientId === row.clientId
                                  ? <Loader2 className="w-3 h-3 animate-spin" />
                                  : "Pull Data"}
                              </button>
                            ) : (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    style={{
                                      ...actionBtn,
                                      background: isActive ? C.goldLight : actionBtn.background as string,
                                      borderColor: isActive ? C.goldBorder : C.cardBorder,
                                      color: isActive ? C.gold : C.textSecond,
                                    }}
                                    onMouseEnter={(e) => { if (!isActive) { (e.currentTarget.style.background = C.goldLight); (e.currentTarget.style.borderColor = C.goldBorder); (e.currentTarget.style.color = C.gold); } }}
                                    onMouseLeave={(e) => { if (!isActive) { (e.currentTarget.style.background = actionBtn.background as string); (e.currentTarget.style.borderColor = C.cardBorder); (e.currentTarget.style.color = C.textSecond); } }}
                                    onClick={() => handleReview(String(row.clientId), row.clientName, (row as { xeroTenantId?: string | null }).xeroTenantId, row.lastPulled, row.health)}>
                                    {isActive ? "Close" : "Review"}
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent>{isActive ? "Close anomaly panel" : "View anomalies"}</TooltipContent>
                              </Tooltip>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Inline Anomaly Drawer — appears to the right of the table */}
        {drawerClient && (
          <AnomalyDrawer
            clientId={drawerClient.clientId}
            clientName={drawerClient.clientName}
            period={period}
            xeroTenantId={drawerClient.xeroTenantId}
            health={drawerClient.health}
            lastPulled={drawerClient.lastPulled}
            scrollToSuspense={drawerClient.scrollToSuspense}
            onClose={() => setDrawerClient(null)}
          />
        )}
      </div>
    </div>
    </DashboardLayout>
  );
}
